import { FC, useEffect, useState } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet-ruler'
// @ts-ignore
import 'leaflet-ruler/src/leaflet-ruler.css'
import { useTranslation } from 'react-i18next'
import { GenericBanner } from 'src/components/GenericBanner'
import i18n from 'src/i18n'

function toDegreesMinutesAndSeconds(coordinate: number, size: number): string {
  const absolute = Math.abs(coordinate)
  const degrees = Math.floor(absolute)
  const minutesNotTruncated = (absolute - degrees) * 60
  return degrees.toString().padStart(size, '0') + '\u00B0' + minutesNotTruncated.toFixed(3).padStart(6, '0')
}

function formatDMSLat(lat: number): string {
  const dms = toDegreesMinutesAndSeconds(lat, 2)
  const cardinal = lat >= 0 ? i18n.t('direction_north', { ns: 'common' }) : i18n.t('direction_south', { ns: 'common' })
  return `\u00A0\u00A0${dms} ${cardinal}`
}

function formatDMSLng(lng: number): string {
  const dms = toDegreesMinutesAndSeconds(lng, 3)
  const cardinal = lng >= 0 ? i18n.t('direction_east', { ns: 'common' }) : i18n.t('direction_west', { ns: 'common' })
  return `${dms} ${cardinal}`
}

export const MapControls: FC = () => {
  const map = useMap()
  const { t } = useTranslation(['map'])
  const [showRulerBanner, setShowRulerBanner] = useState(false)

  useEffect(() => {
    // Distance ruler
    const ruler = (L.control as any).ruler({
      position: 'topright',
      circleMarker: { color: 'red', radius: 2 },
      lineStyle: { color: 'red', dashArray: '1,6' },
      lengthUnit: { display: 'm', decimal: 2, factor: 1000, label: 'Avstand:' },
      angleUnit: { display: '\u00B0', decimal: 2, factor: 0, label: 'Grader:' },
    })
    ruler.addTo(map)

    // Elevate the overlay pane so ruler lines/markers draw above custom panes (cages etc.)
    const overlayPane = map.getPane('overlayPane')
    const originalZIndex = overlayPane?.style.zIndex ?? ''
    const onRulerToggle = () => {
      if (overlayPane) {
        const isActive = ruler._choice
        overlayPane.style.zIndex = isActive ? '9999' : originalZIndex
      }
      setTimeout(() => {
        setShowRulerBanner(!!ruler._choice)
      }, 0)
    }
    ruler._container?.addEventListener('click', onRulerToggle)

    // Mouse position (coordinates in DMS)
    const MousePosition = L.Control.extend({
      options: { position: 'bottomright' },
      onAdd(m: L.Map) {
        const container = L.DomUtil.create('div', 'leaflet-control-mouseposition')
        container.style.cssText =
          'background:rgba(255,255,255,0.85);padding:2px 6px;font-size:11px;line-height:1.5;border-radius:4px;pointer-events:none;'
        L.DomEvent.disableClickPropagation(container)
        m.on('mousemove', (e: L.LeafletMouseEvent) => {
          container.innerHTML = `${formatDMSLat(e.latlng.lat)}<br/>${formatDMSLng(e.latlng.lng)}`
        })
        return container
      },
    })
    const mousePosition = new MousePosition()
    mousePosition.addTo(map)

    return () => {
      ruler._container?.removeEventListener('click', onRulerToggle)
      if (overlayPane) overlayPane.style.zIndex = originalZIndex
      ruler.remove()
      mousePosition.remove()
    }
  }, [map])

  return (
    <>
      {showRulerBanner && (
        <GenericBanner
          message={t('deactivate-measuring', { ns: 'map' })}
          onClose={() => setShowRulerBanner(false)}
          autoCloseDuration={5000}
          variant="info"
        />
      )}
    </>
  )
}
